"use client";

import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import environmentManifest from "@/config/environments.json";

export type RuntimeEnvironment = "development" | "test" | "production";

declare global {
  interface Window {
    __ASYMMETRIC_ENVIRONMENT__?: RuntimeEnvironment;
  }
}

const environmentOrder = environmentManifest.order as RuntimeEnvironment[];
const environmentProfiles = environmentManifest.profiles;
const environmentSummaries: Record<RuntimeEnvironment, string> = {
  development: "Editable data · Orders off",
  test: "Isolated local fixtures",
  production: "Hosted owner data · Approval required",
};

function isRuntimeEnvironment(value: unknown): value is RuntimeEnvironment {
  return value === "development" || value === "test" || value === "production";
}

function configuredEnvironment() {
  if (typeof window === "undefined") return null;
  const configured = window.__ASYMMETRIC_ENVIRONMENT__
    ?? document.documentElement.dataset.environment;
  return isRuntimeEnvironment(configured) ? configured : null;
}

function subscribeToEnvironment() {
  return () => {};
}

function serverEnvironment() {
  return null;
}

export default function EnvironmentSwitcher({ className = "", screen = "agent" }: { className?: string; screen?: string }) {
  const current = useSyncExternalStore(
    subscribeToEnvironment,
    configuredEnvironment,
    serverEnvironment,
  );
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const currentProfile = current ? environmentProfiles[current] : null;

  function navigateOptions(event: React.KeyboardEvent<HTMLElement>) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const key = event.key;
    if (!open) setOpen(true);
    window.requestAnimationFrame(() => {
      const links = Array.from(rootRef.current?.querySelectorAll<HTMLAnchorElement>(".environment-menu a") ?? []);
      const index = links.indexOf(document.activeElement as HTMLAnchorElement);
      const next = key === "Home" ? 0 : key === "End" ? links.length - 1 : index < 0 ? (key === "ArrowUp" ? links.length - 1 : 0) : (index + (key === "ArrowDown" ? 1 : -1) + links.length) % links.length;
      links[next]?.focus();
    });
  }

  useEffect(() => {
    if (!open) return;

    function closeOnOutsidePointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <nav
      ref={rootRef}
      className={`environment-switcher ${className}`.trim()}
      aria-label="Runtime environment"
      data-current-environment={current ?? "loading"}
    >
      <button
        ref={triggerRef}
        className="environment-trigger"
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={!currentProfile}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={navigateOptions}
      >
        <span className="environment-current-label">
          {currentProfile?.label ?? "Environment"}
        </span>
        <span className="environment-current-code">
          {currentProfile?.shortLabel ?? "Loading"}
        </span>
        <ChevronDownIcon className="environment-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="environment-menu" id={menuId}>
          {environmentOrder.map((environment) => {
            const profile = environmentProfiles[environment];
            const active = current === environment;
            const summary = `${active ? "Current · " : ""}${environmentSummaries[environment]}`;
            return (
              <a
                className="environment-option"
                onKeyDown={navigateOptions}
                key={environment}
                href={`${profile.url}#${screen}`}
                aria-current={active ? "page" : undefined}
                data-environment-option={environment}
                title={`${profile.label} · ${profile.dataBoundary} · ${profile.operationPolicy}`}
                onClick={event => { if (active) event.preventDefault(); setOpen(false); }}
              >
                <span className="environment-option-code">{profile.shortLabel}</span>
                <span className="environment-option-copy">
                  <strong>{profile.label}</strong>
                  <small>{summary}</small>
                </span>
                {active && <CheckIcon className="environment-check" aria-hidden="true" />}
              </a>
            );
          })}
        </div>
      )}
    </nav>
  );
}
